import { KeyboardEvent, memo, useState } from 'react'

import { UnfoldMore } from '@mui/icons-material'
import { InputAdornment, TextField } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { audioStreamQueryKeys, videoStreamQueryKeys } from 'services/queryKeys'

import { Stream, StreamPickerDialogContent } from './StreamPickerDialogContent'

export type StreamSelectorProps = {
  repositoryId?: string
  kind: 'video' | 'audio'
  streamId?: string
  disabled?: boolean
  onChange: (streamId: string) => void
}

const StreamSelectorRaw = ({ repositoryId, kind, streamId, disabled, onChange }: StreamSelectorProps) => {
  const { t } = useTranslation(['common', 'domain'])

  const [dialogOpen, setDialogOpen] = useState(false)
  // Keeps the just-picked stream's name on screen — its per-id detail query isn't cached yet.
  const [pickedStream, setPickedStream] = useState<Stream>()

  const isVideo = kind === 'video'

  // Resolve the currently-selected stream so we can show its name (not just the id).
  const videoDetailQuery = useQuery({
    ...videoStreamQueryKeys.detail({ streamId: streamId! }),
    enabled: isVideo && !!streamId,
  })
  const audioDetailQuery = useQuery({
    ...audioStreamQueryKeys.detail({ streamId: streamId! }),
    enabled: !isVideo && !!streamId,
  })
  const detailData = (isVideo ? videoDetailQuery.data : audioDetailQuery.data) ?? null

  const selected = (pickedStream?.id === streamId ? pickedStream : undefined) ?? detailData
  const displayValue = streamId ? selected?.name || streamId : ''

  if (disabled) {
    return (
      <TextField
        size="small"
        fullWidth
        disabled
        label={t('domain:Workspace.Event.ConfigEditor.stream')}
        value={displayValue}
        helperText={t('domain:Workspace.Event.ConfigEditor.advancedStreamHint')}
      />
    )
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      // Enter would otherwise submit the surrounding config form.
      event.preventDefault()
      setDialogOpen(true)
    }
  }

  return (
    <>
      <TextField
        size="small"
        fullWidth
        label={t('domain:Workspace.Event.ConfigEditor.stream')}
        placeholder={t('domain:Workspace.Event.ConfigEditor.noStreamSelected')}
        value={displayValue}
        onClick={() => setDialogOpen(true)}
        onKeyDown={handleKeyDown}
        sx={{ '& .MuiInputBase-root, & .MuiInputBase-input': { cursor: 'pointer' } }}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <UnfoldMore fontSize="small" />
              </InputAdornment>
            ),
          },
          inputLabel: { shrink: true },
        }}
      />

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={t(
          isVideo
            ? 'domain:Workspace.Event.ConfigEditor.selectVideoStreamTitle'
            : 'domain:Workspace.Event.ConfigEditor.selectAudioStreamTitle',
        )}
      >
        <StreamPickerDialogContent
          repositoryId={repositoryId}
          kind={kind}
          streamId={streamId}
          onSelect={(stream) => {
            setPickedStream(stream)
            onChange(stream.id)
          }}
        />
      </Dialog>
    </>
  )
}

export const StreamSelector = memo(StreamSelectorRaw)
